import { useState } from 'react';
import type { FormType } from '@/types/form';
import { FieldPreview } from './FieldPreview';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Save, Send, Eye } from 'lucide-react';

interface FormPreviewProps {
  form: FormType;
  onClose: () => void;
  onSave?: () => void;
}

export const FormPreview: React.FC<FormPreviewProps> = ({
  form,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    console.log('Form submission:', { formId: form.id, data: formData });
    alert('Form submitted successfully! (This is just a preview)');
    
    setIsSubmitting(false);
  };

  const sortedFields = [...form.fields].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={onClose}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-xl font-semibold flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Form Preview
              </h1>
              <p className="text-sm text-muted-foreground">{form.name}</p>
            </div>
            <Badge variant="secondary" className="text-xs">
              Preview Mode
            </Badge>
          </div>

          <div className="flex items-center gap-2">
            {onSave && (
              <Button variant="outline" size="sm" onClick={onSave}>
                <Save className="h-4 w-4 mr-2" />
                Save Form
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Preview Content */}
      <div className="flex-1 overflow-y-auto bg-muted/30 p-6">
        <div className="max-w-2xl mx-auto">
          <Card className="shadow-lg">
            <CardHeader className="bg-primary text-primary-foreground">
              <CardTitle className="text-2xl">{form.name}</CardTitle>
              {form.description && (
                <p className="text-primary-foreground/90 mt-2">{form.description}</p>
              )}
              <div className="flex items-center gap-4 text-sm text-primary-foreground/80 mt-3">
                <span className="capitalize">{form.category}</span>
                <span>•</span>
                <span>Version {form.version}</span>
                {form.metadata.settings.requireAuthentication && (
                  <>
                    <span>•</span>
                    <Badge variant="secondary" className="text-xs">
                      Login Required
                    </Badge>
                  </>
                )}
              </div>
            </CardHeader>

            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {sortedFields.map((field) => (
                  <FieldPreview
                    key={field.id}
                    field={field}
                    value={formData[field.name]}
                    onChange={(value) => handleFieldChange(field.name, value)}
                    disabled={false}
                  />
                ))}

                {sortedFields.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground">
                    <div className="text-lg font-medium mb-2">No fields added</div>
                    <p className="text-sm">Add some fields to see the form preview</p>
                  </div>
                )}

                {/* Form Footer */}
                {sortedFields.length > 0 && (
                  <div className="pt-6 border-t border-border">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-muted-foreground">
                        <span className="font-medium">{sortedFields.filter(f => f.required).length}</span> required fields
                      </div>
                      
                      <div className="flex gap-3">
                        {form.metadata.settings.enableDrafts && (
                          <Button type="button" variant="outline" disabled={isSubmitting}>
                            Save as Draft
                          </Button>
                        )}
                        <Button type="submit" disabled={isSubmitting}>
                          {isSubmitting ? (
                            'Submitting...'
                          ) : (
                            <>
                              <Send className="h-4 w-4 mr-2" />
                              Submit Form
                            </>
                          )}
                        </Button>
                      </div>
                    </div>

                    {form.metadata.settings.allowMultipleSubmissions && (
                      <p className="text-xs text-muted-foreground mt-2">
                        You can submit this form multiple times
                      </p>
                    )}
                  </div>
                )}
              </form>
            </CardContent>
          </Card>

          {/* Preview Info */}
          <Card className="mt-6 border-2 border-dashed border-muted-foreground/20">
            <CardContent className="p-4">
              <div className="text-center text-sm text-muted-foreground">
                <p className="font-medium mb-1">This is a preview of your form</p>
                <p>Form submissions in preview mode are not saved</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};